import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useCurrentUser, type AppRole } from "@/hooks/useCurrentUser";
import { Button } from "@/components/ui/button";
import { AUTH0_AUDIENCE } from "@/lib/config";
import { ExternalLink, Loader2, LogIn } from "lucide-react";

interface Props {
  children: React.ReactNode;
  allowedRoles?: AppRole[];
}

export default function ProtectedRoute({ children, allowedRoles }: Props) {
  const { isAuthenticated, isLoading, loginWithPopup, loginWithRedirect } = useAuth0();
  const { data: currentUser, isLoading: userLoading } = useCurrentUser();
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const isEmbeddedPreview = window.self !== window.top;
  const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  useEffect(() => {
    if (isLoading || isAuthenticated || isEmbeddedPreview) return;

    void loginWithRedirect({
      appState: { returnTo },
      authorizationParams: { audience: AUTH0_AUDIENCE },
    });
  }, [isAuthenticated, isEmbeddedPreview, isLoading, loginWithRedirect, returnTo]);

  const handleEmbeddedLogin = async () => {
    setIsAuthenticating(true);

    try {
      await loginWithPopup({
        authorizationParams: { audience: AUTH0_AUDIENCE },
      });
    } catch (error) {
      console.error("Auth0 popup login failed in embedded preview", error);

      await loginWithRedirect({
        appState: { returnTo },
        authorizationParams: { audience: AUTH0_AUDIENCE },
        openUrl: async (url) => {
          window.open(url, "_blank", "noopener,noreferrer");
        },
      });
    } finally {
      setIsAuthenticating(false);
    }
  };

  // When role-gating is needed, wait for backend user to load before deciding
  const rolesReady = !allowedRoles || !isAuthenticated || !userLoading;

  if (isLoading || (!isAuthenticated && !isEmbeddedPreview) || !rolesReady) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6 py-10">
        <div className="w-full max-w-md space-y-6 rounded-3xl border border-border bg-card p-8 text-center shadow-sm">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-primary/10 text-primary">
            <LogIn className="h-6 w-6" />
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-semibold text-foreground">Entrar no preview</h1>
            <p className="text-sm text-muted-foreground">
              O Auth0 bloqueia login por redirecionamento dentro do iframe do editor. Use o popup abaixo para autenticar sem sair do preview.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <Button onClick={() => void handleEmbeddedLogin()} disabled={isAuthenticating} className="w-full">
              {isAuthenticating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Abrindo login...
                </>
              ) : (
                "Entrar com Auth0"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => window.open(window.location.href, "_blank", "noopener,noreferrer")}
            >
              <ExternalLink className="mr-2 h-4 w-4" />
              Abrir preview em nova aba
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const userRoles = (currentUser?.roles ?? []) as AppRole[];
  if (allowedRoles && !allowedRoles.some((r) => userRoles.includes(r))) {
    return <Navigate to="/aguardando" replace />;
  }

  return <>{children}</>;
}

